import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {CompanyPlanDto} from '../../shared/dtos/company-plan.dto';
import {getCompanyPlan} from '../../shared/services/company-plan.service';
import {isAuthRedirectError} from '../../shared/services/auth.service';
import {logError} from '../../shared/services/logger.service';
import {allowance, count, planMoney, planPrice} from '../../shared/pricing/plan-pricing';
import {renewsOn} from '../settings/plan-choice';
import {Hairline, NoticeBox, PrimaryPill, SmallCaps} from './AccountKit';

/*
 * Your plan (Android's AccountScreen.kt PlanCard), read from the server
 * (SQ-415, GET api/v1/billing). Choosing or changing a plan, adding
 * estimates, the payment method and receipts all live on the plan page
 * (/settings/plan), whose card details are only ever typed on Stripe's
 * pages; this card says where things stand and opens it.
 */

export const PlanSummary = ({locked}: {locked: boolean}) => {
  const navigate = useNavigate();
  const [data, setData] = useState<CompanyPlanDto | null>();
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    getCompanyPlan(navigate)
      .then(d => { if (active) setData(d); })
      .catch(e => {
        if (!active || isAuthRedirectError(e)) return;
        logError('Could not read the plan', {}, e as Error);
        setError(true);
        setData(null);
      });
    return () => { active = false; };
  }, [navigate]);

  const openPlan = <PrimaryPill text="See your plan" onClick={() => navigate('/settings/plan')} disabled={locked}/>;

  if (data === undefined) {
    return <div className="ac-plan-head"><div className="ac-sub">Loading your plan…</div></div>;
  }
  if (data === null) {
    return (
      <>
        <div className="ac-plan-head"><div className="ac-sub">{error ? 'Could not load your plan.' : ''}</div></div>
        <div className="ac-pad-pill">{openPlan}</div>
      </>
    );
  }

  const {plan, usage, catalog, paymentMethod} = data;
  const annual = plan?.cycle === 'annual';
  const allowed = usage.allowance ?? 0;
  const left = allowed - usage.used;
  const over = left < 0;
  const pct = allowed > 0 ? Math.min(100, Math.round((usage.used / allowed) * 100)) : 0;
  const runningLow = pct >= catalog.lowPoolAt * 100;
  const failing = plan?.status === 'past_due' || plan?.status === 'unpaid';

  return (
    <>
      <div className="ac-plan-head">
        <div className="ac-grow">
          <div className="ac-plan-title">
            {plan ? `${count(allowance(plan.perMonth, annual))} estimates ${annual ? 'a year' : 'a month'}` : 'No plan yet'}
          </div>
          <div className="ac-sub">
            {plan
              ? [annual ? 'Billed yearly' : 'Billed monthly', `${plan.cancelAtPeriodEnd ? 'Ends' : 'Renews'} ${renewsOn(plan)}`].join(' · ')
              : `${count(usage.used)} estimate${usage.used === 1 ? '' : 's'} made this month`}
          </div>
        </div>
        {plan && <div className="ac-plan-price">{planMoney(planPrice(catalog, plan.perMonth, annual))} / {annual ? 'yr' : 'mo'}</div>}
      </div>
      {failing && <NoticeBox text="The last payment did not go through. Update the payment method on your plan page."/>}
      {data.problem && <NoticeBox text={data.problem}/>}
      <div className="ac-pad-pill">{openPlan}</div>

      {plan && allowed > 0 && (
        <div className="ac-lower">
          <SmallCaps text={annual ? 'Estimates this year' : 'Estimates this month'}/>
          <div className="ac-usage-line">
            <span>{count(usage.used)} of {count(allowed)} used</span>
            <span className={over ? 'ac-usage-left ac-usage-left--over' : 'ac-usage-left'}>{over ? `${count(-left)} over` : `${count(left)} left`}</span>
          </div>
          <div className="ac-usage-bar">
            <span style={{width: `${pct}%`, background: over ? 'var(--ac-red)' : runningLow ? 'var(--ac-amber)' : 'var(--ac-green)'}}/>
          </div>
        </div>
      )}

      <Hairline/>
      <div className="ac-lower">
        <SmallCaps text="Payment method"/>
        <div className="ac-sub">{paymentMethod ? [paymentMethod.label, paymentMethod.sub].filter(Boolean).join(' · ') : 'No payment method yet'}</div>
      </div>
    </>
  );
};
